import {
  Box,
  Modal,
  RadioGroup,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/it";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditionCard } from "./EditionCard.component";
import { getModalEditionChangeStyles } from "./ModalEditionChange.style";
import { ModalEditionChangeProps } from "./ModalEditionChange.types";
import type { EditionWithSessions } from "../../types.d";
import { ButtonText } from "../Button";

export function ModalEditionChange({
  open,
  handleClose,
  enrollableEditionsWithSessions,
  onEditionChange,
  isVirtual,
  isLoading,
  isPollingEditionChange
}: ModalEditionChangeProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [selectedEditionId, setSelectedEditionId] = useState<number | null>(null);
  const [expandedEditionId, setExpandedEditionId] = useState<number | null>(null);

  const styles = getModalEditionChangeStyles(theme);

  dayjs.locale(i18n.language);

  const handleEnroll = () => {
    if (selectedEditionId !== null) {
      onEditionChange(selectedEditionId);
    }
  };

  const toggleAccordion = (editionId: number) => {
    setExpandedEditionId(expandedEditionId === editionId ? null : editionId);
  };

  const canEnroll = (editionWithSessions: EditionWithSessions): boolean => {
    return editionWithSessions.remainingPlaces > 0;
  };

  const modalTitle = isVirtual ? t("virtual_classroom_registration") : t("live_classroom_registration").split(":")[0];

  return (
    <Modal
      open={ open }
      onClose={ handleClose }
      aria-labelledby="modal-edition-change-title"
      aria-describedby="modal-edition-change-description"
      sx={ styles.modalEditionChange.modal }
    >
      <Box sx={ styles.modalEditionChange.modalBox }>
        <Box>
          <Box sx={ styles.modalEditionChange.headerBox }>
            <Typography id="modal-edition-change-title" variant="h5" sx={ styles.modalEditionChange.headerTitle }>
              { modalTitle }
            </Typography>
          </Box>

          <Box sx={ styles.modalEditionChange.contentBox }>
            <RadioGroup value={ selectedEditionId } onChange={ (e) => setSelectedEditionId(Number(e.target.value)) }>
              { enrollableEditionsWithSessions?.
                sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                .map((editionWithSessions) => (
                  <EditionCard
                    key={ editionWithSessions.id }
                    editionWithSessions={ editionWithSessions }
                    isVirtual={ !!isVirtual }
                    disabled={ !canEnroll(editionWithSessions) }
                    isExpanded={ expandedEditionId === editionWithSessions.id }
                    onToggleAccordion={ () => toggleAccordion(editionWithSessions.id) }
                    theme={ theme }
                    t={ t }
                  />
                )) }
            </RadioGroup>
          </Box>
        </Box>

        <Box sx={ styles.modalEditionChange.footerBox }>
          <ButtonText
            variant="outlined"
            onClick={ handleClose }
            sx={ styles.modalEditionChange.cancelButton }
          >
            { t("annul_enroll") }
          </ButtonText>
          <ButtonText
            disabled={ selectedEditionId === null || isLoading || isPollingEditionChange }
            onClick={ handleEnroll }
            sx={ styles.modalEditionChange.enrollButton }
          >
            { t("enroll_edition") }
          </ButtonText>
        </Box>
      </Box>
    </Modal>
  );
}
